/**
 * Zod schema for the `persons` collection (ADR-136).
 *
 * Durable identity record — one per real-world person, claimed or not. Replaces
 * the dual-record model where claimed people lived in `users/{uid}` and
 * pre-auth people lived in `contacts/{id}`. PersonId is a generated nanoid,
 * immutable across the person's lifecycle. The Firebase Auth uid lives on
 * `persons.authUid` (null until claimed) and as the doc ID of the
 * `authAccounts/{uid}` pointer.
 *
 * Public tier — readable by any authenticated event member.
 *
 * See ADR-002 for the three-tier privacy architecture this maps onto, and
 * ADR-083 for the schema-as-source-of-truth contract.
 */

import { z } from 'zod';
import type { TimestampLike } from '../types/common';
import { roleSchema } from './role';

export const privacySettingsSchema = z.object({
  /**
   * Binary toggle: appear in the attendee directory with full profile
   * visible, or be completely hidden. Default: false (opt-in).
   */
  showInDirectory: z.boolean(),
});

export const personDocumentSchema = z.object({
  /**
   * Canonical lowercased email. Primary match key for activation
   * (matching `auth.token.email`) and for organizer-side dedup before
   * person creation.
   */
  email: z.string(),

  /**
   * Global role. Mirrors the Auth custom claim once claimed; for unclaimed
   * persons this is the organizer-set planned role.
   */
  role: roleSchema,

  /**
   * Firebase Auth uid, set when the person authenticates for the first
   * time. Null until claimed. The corresponding `authAccounts/{uid}` doc
   * holds the reverse pointer.
   */
  authUid: z.string().nullable(),

  /** Stamped by activateAccount when authUid is first set. */
  claimedAt: z.custom<TimestampLike>().nullable(),

  /** CF-computed from firstName + lastName + honorifics. */
  displayName: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  honorificPrefix: z.string().nullable(),
  honorificSuffix: z.string().nullable(),

  photoURL: z.string().nullable(),

  /**
   * Where the current photo came from (ADR-180): 'app' = uploaded through
   * the app (organizer or self-service) — eligible for write-back to the
   * FRG Headshot Library when the person is HubSpot-linked;
   * 'headshot-library' = imported FROM the library — the write-back trigger
   * skips these (loop break). Absent on pre-ADR-180 documents.
   */
  photoOrigin: z.enum(['app', 'headshot-library']).optional(),

  privacySettings: privacySettingsSchema,

  /**
   * Denormalized from `personProfiles` when `privacySettings.showInDirectory`
   * is on. CF-managed; protected from client writes by the rules layer.
   */
  publicEmail: z.string().nullable(),
  publicLinkedIn: z.string().nullable(),
  publicBio: z.string().nullable(),
  publicTitle: z.string().nullable(),
  publicOrganization: z.string().nullable(),
  publicWebsite: z.string().nullable(),
  publicWorkCity: z.string().nullable(),
  publicWorkState: z.string().nullable(),

  /** Set by setUserRole / role-sync paths. */
  roleUpdatedAt: z.custom<TimestampLike>().nullable(),

  /**
   * PersonId of the organizer who created this person record. Tracks
   * provenance for bulk imports and direct-creates.
   */
  createdByUserId: z.string(),
});
